

import {
  baseServiceUrl,
  EUserType,
  ISTESTCENTREADMINPANEL,
  urls,
} from "../environment";
import toast from "react-hot-toast";
import { useRestService } from "../hooks/useRestService";
import {
  UserContextProps,
  UserContextProviderProps,
} from "../types/UserContextTypes";
import { useNavigate } from "react-router-dom";
import { ExamThumbnailTypes } from "../interfaces/exam.interface";
import {  decryptAES256CBC2, decryptWithFixedKey, encryptWithFixedKey, generateRandomBytes } from "../utils";
import useUserDataStore from "../stores/useUserDataStore";
import useAppLevelStores from "../stores/useAppLevelStores";
import useExamStore from "../stores/useExamStore";
import useCustomerStore from "../stores/useCustomerStore";


const useUserDataService=():UserContextProps=>{
  const restService = useRestService(baseServiceUrl);
  const appLevelContext = useAppLevelStores();
  const examContext = useExamStore()
  const customerContext = useCustomerStore()
  const userDataStore  = useUserDataStore();

  //   ******************************   Admin Login Handler ****************************  //
  const adminLoginHandler = async ({
    username,
    password,
    org_id,
    user_type,
    finallyCallback,
    successCallback,
  }: {
    username: string;
    password: string;
    org_id: number | null;
    user_type: string | null;
    successCallback?: (args?: any) => void;
    finallyCallback?: (args?: any) => void;
  }) => {
    if (
      username === "" ||
      username.trim() === "" ||
      password === "" ||
      password.trim() === ""
    ) {
      toast.error("All fields are required");
      return;
    }
    try {
      appLevelContext.setIsLoading(true);
      const encryption_key = generateRandomBytes();

      const payload = encryptWithFixedKey({
        user: {
          email: username,
          password: password,
          org_id: org_id,
          user_type: user_type,
        },
        encryption_key,
      });

      const response = await restService.post(
        baseServiceUrl + urls.adminLoginUrl,
        payload
      );
      if (response?.data) {
        const rawResponse = await decryptAES256CBC2(
          response.data?.response,
          btoa(encryption_key)
        );
        const responsedData = JSON.parse(rawResponse);

        if (
          responsedData &&
          responsedData?.user &&
          responsedData?.user?.userType
        ) {
          userDataStore.setCurrUserType(responsedData.user.userType);
          if (
            ISTESTCENTREADMINPANEL === "OFFLINE" &&
            responsedData?.user?.userType !== EUserType.LOCALSERVERADMIN
          ) {
            appLevelContext.setIsLoading(false);
            return toast.error("Invalid user");
          } else if (
            ISTESTCENTREADMINPANEL === "ONLINE" &&
            responsedData?.user?.userType &&
            ![
              EUserType.CENTRALADMIN,
              EUserType.CUSTMONADMIN,
              EUserType.CUSTADMIN,
              EUserType.MONADMIN,
            ].includes(responsedData?.user?.userType)
          ) {
            appLevelContext.setIsLoading(false);
            return toast.error("Invalid user");
          }

          if (
            responsedData?.user?.userType &&
            [EUserType.CUSTADMIN, EUserType.CUSTMONADMIN].includes(
              responsedData?.user?.userType
            )
          ) {
            appLevelContext.setExpandedMenu([
              `/admin/exams/view-exam-group`,
              "/admin/exams/view-exams",
            ]);
            if (responsedData?.user?.orgId)
              await getOrganizationDetailsByID({
                orgId: responsedData?.user?.orgId,
              });
          }
        } else {
          userDataStore.setCurrUserType(null);
        }
        userDataStore.setCurrUserType(responsedData.user.userType);

        if (responsedData && !!responsedData.user) {
          successCallback?.(responsedData?.user);
          userDataStore.setUserDetails(responsedData.user);
          userDataStore.setToken(responsedData.user.token);
          userDataStore.setislogin(true);
          userDataStore.setisAdmin(true);
          userDataStore.setCurrUserType(responsedData.user.userType);
        } else {
        }
      }
    } catch (err: any) {
      //   setloginbtn(false);
      toast.error(
        (err && err?.response?.data?.message) ||
          err?.message ||
          "Error encounter. Please try again."
      );
    } finally {
      appLevelContext.setIsLoading(false);
      finallyCallback?.();
    }
  };

  const checkForLoginTypes = async ({
    email,
    finallyCallback,
  }: {
    email: string;
    finallyCallback?: (args?: any) => void;
  }) => {
    if (email === "" || email.trim() === "") {
      toast.error("Email ID is required");
      return;
    }

    try {
      appLevelContext.setIsLoading(true);

      const response = await restService.post(
        baseServiceUrl + urls.loginUserTypesByEmail,
        {
          email,
          is_examinee: 0,
        }
      );
      if (!!response && response.data) {
        finallyCallback?.(response.data);
      }
    } catch (err: any) {
      toast.error(
        (err && err?.response?.data?.message) ||
          err?.message ||
          "Error encounter. Please try again."
      );
    } finally {
      appLevelContext.setIsLoading(false);
    }
  };

  const resetUserContext = () => {
    userDataStore.setUserDetails(null);
    userDataStore.setToken("");
    userDataStore.setisTCAdmin(false);
    userDataStore.setisAdmin(false);
    userDataStore.setislogin(false);
    userDataStore.setOpenExamDialog(false);
    userDataStore.setQuestionSynced(false);
    userDataStore.setCurrUserType(null);
    userDataStore.setCurrentOrganization(null);
  };
const initUserDetails = ()=>{
    if (
      userDataStore.userdetails &&
      userDataStore.userdetails?.userType &&
      [EUserType.CENTRALADMIN, EUserType.MONADMIN].includes(
        userDataStore.userdetails.userType
      )
    ) {
      userDataStore.setIsSuperAdminGroup(true);
    } else {
      userDataStore.setIsSuperAdminGroup(false);
    }
    if (
      userDataStore.userdetails &&
      userDataStore.userdetails?.userType &&
      [EUserType.CUSTMONADMIN, EUserType.MONADMIN].includes(
        userDataStore.userdetails?.userType
      )
    ) {
      userDataStore.setIsMonitorAdminGroup(true);
    } else {
      userDataStore.setIsMonitorAdminGroup(false);
    }

}


  //   ********************************  Get Organization Details By ID  ************************************************
  const getOrganizationDetailsByID = async ({
    orgId,
    token,
    errorCallback,
    finallyCallback,
    successCallback,
  }: {
    orgId: number;
    token?: string;
    successCallback?: (args?: any) => void;
    errorCallback?: (args?: any) => void;
    finallyCallback?: (args?: any) => void;
  }) => {
    try {
      if (token) {
        appLevelContext.setIsLoading(true);

        const response = await restService.get(
          `${urls.getOrgDetailsById}/${orgId}`,
          token
        );
        successCallback?.();
        appLevelContext.setIsLoading(false);
        userDataStore.setCurrentOrganization(response?.data);
        return response?.data;
      }
    } catch (error) {
      appLevelContext.setIsLoading(false);
      errorCallback?.();
    } finally {
      finallyCallback?.();
    }
  };

  const getThumbnailHandler = async ({
    payload,
    successCallback,
    errorCallback,
    finallyCallback,
  }: {
    payload: any;
    successCallback?: (args?: any) => void;
    errorCallback?: (args?: any) => void;
    finallyCallback?: (args?: any) => void;
  }) => {
    try {
      appLevelContext.setIsLoading(true);
      const response = await restService.post(
        urls.thumbnailList,
        payload,
        userDataStore.userdetails.token
      );
      return response?.data;
      successCallback?.();
    } catch (error) {
      errorCallback?.();
    } finally {
      appLevelContext.setIsLoading(false);
      finallyCallback?.();
    }
  };

  const initUserService = () => {
    if (
      userDataStore.userdetails &&
      userDataStore.currUserType &&
      [EUserType.CENTRALADMIN, EUserType.CUSTADMIN].includes(
        userDataStore.currUserType
      )
    ) {
      (async () => {
        const response = await getThumbnailHandler({
          payload: { fileType: "exam_thumbnail" },
        });
        if (response && response?.file_data) {
          userDataStore.setThumbnailDetails(response.file_data);
        }
      })();
    }
  };

  const updatePasswordForCustomerAdmin = async ({
    payload,
    successCallback,
    errorCallback,
    finallyCallback,
  }: {
    payload: { user_id: number; password: string };
    successCallback?: (args?: any) => void;
    errorCallback?: (args?: any) => void;
    finallyCallback?: (args?: any) => void;
  }) => {
    try {
      appLevelContext.setIsLoading(true);
      await restService.post(
        urls.updatePassword,
        payload,
        userDataStore.userdetails.token
      );
      successCallback?.();
    } catch (error) {
      toast.error(`Something went wrong`);
      errorCallback?.();
    } finally {
      finallyCallback?.();
      appLevelContext.setIsLoading(false);
    }
  };

  return {
    resetUserContext,
    adminLoginHandler,
    getOrganizationDetailsByID,
    updatePasswordForCustomerAdmin,
    checkForLoginTypes,
    initUserDetails,
    initUserService,
  };
};


export default useUserDataService;